// Edge Verification Card — compares live performance vs backtest expectations.
// Shows z-scores, verdicts, and per-sleeve tracking for the Analytics page.
// Matches existing analytics page styling (plain table inside .table-wrap).

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { apiVps } from "../api/client";
import { algos } from "../api/endpoints";
import type { Algo } from "../api/types";

interface EdgeAttestation {
  algo_id: number;
  preset: string;
  strategy: string;
  live_n: number;
  live_avg_pnl: number;
  expected_avg_pnl: number;
  z_score: number;
  live_sharpe: number;
  expected_sharpe: number;
  live_win_rate: number;
  expected_win_rate: number;
  live_dd: number;
  expected_dd: number;
  verdict: string;
  note: string;
  ts: string;
  source: string;
  baseline: string;
  baseline_source?: string;
}

const usd = (n: number, opts?: { sign?: boolean }) => {
  if (n === undefined || n === null) return "—";
  const s = Math.abs(n).toFixed(0);
  const sign = opts?.sign && n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}$${Number(s).toLocaleString()}`;
};

const pct = (n: number) => {
  if (n === undefined || n === null) return "—";
  return `${(n * 100).toFixed(0)}%`;
};

const VERDICT_LABEL: Record<string, string> = {
  warming_up: "Warming",
  on_track: "On track",
  outperforming: "Hot",
  below_expected: "Below",
  degraded: "Degraded",
};

export function EdgeVerificationCard({ linkedAlgoIds }: { linkedAlgoIds: Set<number> | null }) {
  const { activeVpsId } = useAuth();

  const edgeQ = useQuery<EdgeAttestation[]>({
    queryKey: ["edge-verification"],
    queryFn: () => apiVps<EdgeAttestation[]>("/edge-verification"),
    refetchInterval: 60_000,
    enabled: !!activeVpsId,
  });

  // Fetch algo list to map IDs to names
  const algosQ = useQuery<Algo[]>({
    queryKey: ["algos"],
    queryFn: algos.list,
    staleTime: 300_000,
    enabled: !!activeVpsId,
  });

  const algoMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of algosQ.data ?? []) {
      m.set(a.id, a.name);
    }
    return m;
  }, [algosQ.data]);

  // Account scoping: keep only attestations for sleeves actively linked to the
  // selected account (or to any non-retired account under ALL). null set →
  // links still loading / fetch failed → fail open unfiltered.
  const data = useMemo(() => {
    const all = edgeQ.data ?? [];
    return linkedAlgoIds == null ? all : all.filter((a) => linkedAlgoIds.has(a.algo_id));
  }, [edgeQ.data, linkedAlgoIds]);

  const degraded = data.filter(d => d.verdict === "degraded");
  const below = data.filter(d => d.verdict === "below_expected");
  const onTrack = data.filter(d => d.verdict === "on_track" || d.verdict === "outperforming");
  const warming = data.filter(d => d.verdict === "warming_up");

  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 8, gap: 6 }}>
        <h2 style={{ margin: 0, fontSize: 13 }}>Edge Verification</h2>
        <div className="spacer" />
        <span className="muted small">
          {data.length} sleeves &middot;{" "}
          {onTrack.length > 0 && <span className="good">{onTrack.length} on track </span>}
          {warming.length > 0 && <span className="muted">{warming.length} warming </span>}
          {below.length > 0 && <span className="warn">{below.length} below </span>}
          {degraded.length > 0 && <span className="bad">{degraded.length} degraded </span>}
        </span>
        {edgeQ.isFetching && <span className="muted small">&#x27F3;</span>}
      </div>

      {data.length === 0 ? (
        <div className="muted small">No edge verification data yet — run some trades first.</div>
      ) : (
        <div className="table-wrap" style={{ maxHeight: 400, overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Sleeve</th>
                <th className="num">Trades</th>
                <th className="num">Avg $</th>
                <th className="num">Expected</th>
                <th className="num">Total $</th>
                <th className="num">z-score</th>
                <th className="num">Live DD</th>
                <th className="num">Expected DD</th>
                <th className="num">Sharpe</th>
                <th className="num">Win%</th>
                <th className="num">Src</th>
                <th className="num">Base</th>
                {/* <th className="num">Source</th> */}
                <th className="num">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map(a => {
                const name = algoMap.get(a.algo_id) || a.preset || `algo_${a.algo_id}`;
                const totalLive = a.live_avg_pnl * a.live_n;
                const expectedTotal = a.expected_avg_pnl * a.live_n;
                const zColor = a.z_score < -2 ? "var(--bad)" : a.z_score < -1 ? "var(--warn)" : a.z_score > 2 ? "var(--good)" : "var(--fg-muted)";
                const isBad = a.verdict === "degraded";
                const isWarn = a.verdict === "below_expected";
                return (
                  <tr key={a.algo_id} style={{ background: isBad ? "var(--bg-bad)" : isWarn ? "var(--bg-warn)" : "transparent" }}>
                    <td className="mono" style={{ fontSize: 11, whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{name}</td>
                    <td className="num">{a.live_n}</td>
                    <td className="num" style={{ color: a.live_avg_pnl > 0 ? "var(--good)" : a.live_avg_pnl < 0 ? "var(--bad)" : "var(--fg-muted)" }}>
                      {usd(a.live_avg_pnl, { sign: true })}
                    </td>
                    <td className="num" style={{ color: "var(--fg-muted)" }}>
                      {usd(a.expected_avg_pnl, { sign: true })}
                    </td>
                    <td className="num" style={{ color: totalLive > expectedTotal ? "var(--good)" : totalLive < expectedTotal ? "var(--bad)" : "var(--fg-muted)" }}>
                      {a.live_n > 0 ? usd(totalLive, { sign: true }) : "—"}
                      <span className="muted small"> / {usd(expectedTotal, { sign: true })}</span>
                    </td>
                    <td className="num" style={{ color: zColor, fontWeight: Math.abs(a.z_score) > 2 ? 600 : 400 }}>
                      {a.z_score.toFixed(2)}
                    </td>
                    <td className="num" style={{ color: a.expected_dd > 0 && a.live_dd > a.expected_dd ? "var(--bad)" : "var(--fg-muted)" }}>
                      {a.live_n > 0 ? usd(a.live_dd) : "—"}
                    </td>
                    <td className="num" style={{ color: "var(--fg-muted)" }}>
                      {usd(a.expected_dd)}
                    </td>
                    <td className="num">
                      <span style={{ color: a.live_sharpe > 0 ? "var(--good)" : "var(--bad)" }}>
                        {a.live_sharpe ? a.live_sharpe.toFixed(1) : "—"}
                      </span>
                      <span className="muted small"> / {a.expected_sharpe.toFixed(1)}</span>
                    </td>
                    <td className="num">
                      <span style={{ color: a.live_win_rate > 0.5 ? "var(--good)" : "var(--bad)" }}>
                        {a.live_n > 0 ? pct(a.live_win_rate) : "—"}
                      </span>
                      <span className="muted small"> / {pct(a.expected_win_rate)}</span>
                    </td>
                    <td className="num">
                      <span className={`pill ${a.source === "live" ? "good" : "dim"}`} style={{ fontSize: 9 }}>{a.source}</span>
                    </td>
                    <td className="num">
                      <span className={`pill ${a.baseline === "backtest" ? "good" : "dim"}`} style={{ fontSize: 9 }}>{a.baseline}</span>
                    </td>
                    {/* <td className="num" style={{ fontSize: 10, color: "var(--fg-muted)", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.baseline_source ?? "—"}
                    </td> */}
                    <td className="num">
                      <span className={`pill ${a.verdict === "on_track" || a.verdict === "outperforming" ? "good" : a.verdict === "below_expected" ? "warn" : a.verdict === "degraded" ? "bad" : "dim"}`}>
                        {VERDICT_LABEL[a.verdict] ?? a.verdict}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {degraded.length > 0 && (
        <div className="info-banner warn" style={{ marginTop: 8 }}>
          {degraded.length} sleeve(s) significantly underperforming backtest expectations (z &lt; -2).
        </div>
      )}
    </div>
  );
}